import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Search, Loader2, AlertCircle, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SmartCityAutocomplete Component
 * 
 * A typeahead/autocomplete for city selection with:
 * - Fuzzy matching for misspellings
 * - "Did you mean..." suggestions
 * - State-scoped suggestions
 * - No free-text values (must select from dropdown)
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * 
 * @param {string} value - Current selected city value
 * @param {function} onChange - Callback (city, state) when a city is selected
 * @param {string} state - State code to scope suggestions to
 * @param {string} placeholder - Input placeholder text
 * @param {boolean} disabled - Whether the input is disabled
 * @param {string} className - Additional className for the wrapper
 * @param {boolean} isDefault - Whether this is a default value (shows muted)
 * @param {string} variant - Visual variant: 'default' | 'welcome' (affects colors)
 * @param {string} dataTestId - Test ID for the input element
 */
export const SmartCityAutocomplete = ({ 
  value, 
  onChange, 
  state, 
  placeholder = "Type city name...",
  disabled = false,
  className = "",
  isDefault = false,
  variant = "default",
  dataTestId
}) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasValidSelection, setHasValidSelection] = useState(!!value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Sync query with external value changes
  useEffect(() => {
    setQuery(value || '');
    setHasValidSelection(!!value);
  }, [value]);

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-suggestion-item]');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        // If user typed but didn't select, reset to last valid value
        if (query && !hasValidSelection && value !== query) {
          setQuery(value || '');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [query, hasValidSelection, value]);

  // Track if user is actively searching (not just initial load)
  const [isUserSearching, setIsUserSearching] = useState(false);

  // Search cities with debounce
  useEffect(() => {
    const searchCities = async () => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const params = { q: query };
        if (state) params.state = state;
        
        const response = await axios.get(`${API}/locations/cities/search`, { params });
        setSuggestions(response.data);
        // Only auto-open if user is actively searching (not on initial load)
        if (isUserSearching) {
          setIsOpen(true);
        }
      } catch (error) {
        console.error('City search failed:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchCities, 200);
    return () => clearTimeout(debounce);
  }, [query, state, isUserSearching]);

  const handleSelect = (city) => {
    setQuery(city.city);
    setHasValidSelection(true);
    onChange(city.city, city.state);
    setIsOpen(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setHasValidSelection(false);
    setHighlightedIndex(-1);
    setIsUserSearching(true); // User is actively typing
    // Clear the selection when user starts typing differently
    if (val !== value) {
      onChange('', '');
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) {
      // If dropdown is closed but we have a query, open it
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && query.length >= 2) {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        } else if (suggestions.length > 0) {
          // Select first item if nothing highlighted
          handleSelect(suggestions[0]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        setQuery(value || '');
        break;
      case 'Tab':
        // Allow Tab to move focus but close dropdown
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Get confidence indicator style
  const getConfidenceStyle = (confidence, matchType) => {
    if (matchType === 'exact' || confidence >= 0.95) {
      return 'text-green-600';
    } else if (matchType === 'fuzzy' || matchType === 'spelling') {
      return 'text-amber-600';
    }
    return 'text-muted-foreground';
  };

  // Get input border style based on validation state
  const getInputStyle = () => {
    if (hasValidSelection && query) {
      return 'border-green-500/50 focus:border-green-500';
    }
    if (query && !hasValidSelection && query.length >= 2) {
      return 'border-amber-500/50 focus:border-amber-500';
    }
    return '';
  };

  // Variant-specific styling
  const isWelcome = variant === 'welcome';
  const dropdownBg = isWelcome ? 'bg-white' : 'bg-popover';
  const hoverBg = isWelcome ? 'hover:bg-slate-50' : 'hover:bg-muted';
  const highlightBg = isWelcome ? 'bg-slate-100' : 'bg-muted';
  const inputBg = isWelcome ? 'bg-white text-slate-900' : '';
  const dropdownText = isWelcome ? 'text-slate-900' : '';
  const mutedText = isWelcome ? 'text-slate-400' : 'text-muted-foreground';
  const emptyText = isWelcome ? 'text-slate-500' : 'text-muted-foreground';

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${mutedText}`} />
        <Input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            setIsUserSearching(true);
            if (query.length >= 2) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? "Select state first" : placeholder}
          disabled={disabled}
          className={`pl-9 pr-8 ${inputBg} ${isDefault && value ? 'text-muted-foreground' : ''} ${getInputStyle()}`}
          autoComplete="off"
          data-testid={dataTestId}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className={`h-4 w-4 animate-spin ${mutedText}`} />
          </div>
        )}
        {!loading && hasValidSelection && query && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Check className="h-4 w-4 text-green-500" />
          </div>
        )}
      </div>
      
      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div 
          ref={listRef}
          className={`absolute z-50 w-full mt-1 ${dropdownBg} ${dropdownText} border rounded-md shadow-lg max-h-60 overflow-auto`}
          role="listbox"
        >
          {suggestions.map((city, idx) => (
            <button
              key={`${city.city}-${city.state}-${idx}`}
              type="button"
              onClick={() => handleSelect(city)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              className={`w-full px-3 py-2 text-left flex items-center justify-between text-sm transition-colors ${
                idx === highlightedIndex ? highlightBg : hoverBg
              }`}
              data-testid={`city-suggestion-${idx}`}
              data-suggestion-item
              role="option"
              aria-selected={idx === highlightedIndex}
            >
              <div className="flex items-center gap-2">
                {city.didYouMean && (
                  <span className="text-xs text-amber-600 font-medium">Did you mean:</span>
                )}
                <span className={city.didYouMean ? 'font-medium' : ''}>{city.city}</span>
              </div>
              <div className="flex items-center gap-2">
                {city.matchType && city.matchType !== 'exact' && city.matchType !== 'prefix' && (
                  <span className={`text-xs ${getConfidenceStyle(city.confidence, city.matchType)}`}>
                    {Math.round(city.confidence * 100)}%
                  </span>
                )}
                <span className={`text-xs ${mutedText}`}>{city.state}</span>
              </div>
            </button>
          ))}
          {/* Keyboard hint */}
          <div className={`px-3 py-1.5 text-xs ${mutedText} border-t flex items-center gap-2`}>
            <span>↑↓ Navigate</span>
            <span>·</span>
            <span>Enter Select</span>
            <span>·</span>
            <span>Esc Close</span>
          </div>
        </div>
      )}
      
      {/* No Results / Prompt */}
      {isOpen && query.length >= 2 && suggestions.length === 0 && !loading && (
        <div className={`absolute z-50 w-full mt-1 ${dropdownBg} border rounded-md shadow-lg p-3`}>
          <div className={`flex items-center gap-2 text-sm ${emptyText}`}>
            <AlertCircle className="h-4 w-4" />
            <span>No cities found. Try a different spelling or check the state.</span>
          </div>
        </div>
      )}

      {/* Validation hint when user typed but hasn't selected */}
      {!isOpen && query && !hasValidSelection && query.length >= 2 && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Please select a city from the suggestions
        </p>
      )}
    </div>
  );
};

export default SmartCityAutocomplete;
